"use client";
import { motion } from "framer-motion";
import { Button } from "@nextui-org/button";
import { useTheme } from "next-themes";

interface ArtistAnalyticsProps {
  onArtistModalOpen: () => void;
}

export default function ArtistAnalytics({ onArtistModalOpen }: ArtistAnalyticsProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section 
      aria-labelledby="artist-analytics-heading"
      className="artist-analytics-section"
    >
      <h2 id="artist-analytics-heading" className="sr-only">Artist Analytics & Insights</h2>
      <div className="relative w-full flex flex-col lg:flex-row items-center justify-between mt-24">
        <div className="border-0 border-blue-700 w-full lg:w-1/2 p-5 lg:p-10">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.1, duration: 0.7, ease: "easeOut" }}
            className="wow fadeInUp lg:max-w-[510px]"
          >
            <span className="mb-4 block text-lg font-medium text-primary md:text-[22px]">For Artists</span>
            <h2 className="mb-4 capitalize text-3xl font-bold text-black dark:text-white sm:text-4xl md:text-[44px] md:leading-tight">Insights & Analytics</h2>
            <p className="mb-[30px] text-base leading-relaxed text-black dark:text-white/80">
              Gain unprecedented insights into your live performances, and fan engagement. Track which moments resonate most with your audience and understand your reach across different venues and cities.
            </p>
            <Button 
              onPress={onArtistModalOpen}
              className="bg-gradient-to-r from-primary to-primary/70 text-white font-medium rounded-lg hover:opacity-90 transition-opacity px-8 py-3"
            >
              Learn More
            </Button>
          </motion.div>
        </div>

        <div className="border-0 border-yellow-800 w-full lg:w-1/2 p-5 lg:p-10">
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
            className="relative flex items-center justify-center border-0 border-blue-700 p-5"
          >
            {/* Analytics-themed glowy lines and arrows */}
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Glowy data flow lines */}
              <motion.div
                className="absolute w-96 h-96"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.path
                    d="M50 200 Q150 100 250 200 T450 200"
                    stroke="url(#analyticsGradient1)"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="5,5"
                    animate={{
                      strokeDashoffset: [0, -10],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  <defs>
                    <linearGradient id="analyticsGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#2485EC" stopOpacity="0.8" />
                      <stop offset="50%" stopColor="#2485EC" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#2485EC" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Animated arrows pointing to data */}
              <motion.div
                className="absolute w-80 h-80"
                animate={{
                  rotate: [0, -360],
                }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 320 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.path
                    d="M160 40 L180 60 L160 80 L140 60 Z"
                    fill="url(#arrowGradient1)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.path
                    d="M280 160 L300 180 L280 200 L260 180 Z"
                    fill="url(#arrowGradient2)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1,
                    }}
                  />
                  <motion.path
                    d="M160 280 L180 300 L160 320 L140 300 Z"
                    fill="url(#arrowGradient3)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 2,
                    }}
                  />
                  <motion.path
                    d="M40 160 L60 180 L40 200 L20 180 Z"
                    fill="url(#arrowGradient4)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 3,
                    }}
                  />
                  <defs>
                    <linearGradient id="arrowGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#2485EC" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#2485EC" stopOpacity="0.3" />
                    </linearGradient>
                    <linearGradient id="arrowGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FEC941" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#FEC941" stopOpacity="0.3" />
                    </linearGradient>
                    <linearGradient id="arrowGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ff00c8" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.3" />
                    </linearGradient>
                    <linearGradient id="arrowGradient4" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#2485EC" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#2485EC" stopOpacity="0.3" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Glowy data points */}
              <motion.div
                className="absolute w-64 h-64"
                animate={{
                  rotate: [360, 0],
                }}
                transition={{
                  duration: 30,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.circle
                    cx="128"
                    cy="64"
                    r="4"
                    fill="#2485EC"
                    animate={{
                      opacity: [0.2, 1, 0.2],
                      scale: [0.5, 1.5, 0.5],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.circle
                    cx="192"
                    cy="128"
                    r="4"
                    fill="#FEC941"
                    animate={{
                      opacity: [0.2, 1, 0.2],
                      scale: [0.5, 1.5, 0.5],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1,
                    }}
                  />
                  <motion.circle
                    cx="128"
                    cy="192"
                    r="4"
                    fill="#ff00c8"
                    animate={{
                      opacity: [0.2, 1, 0.2],
                      scale: [0.5, 1.5, 0.5],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 2,
                    }}
                  />
                  <motion.circle
                    cx="64"
                    cy="128"
                    r="4"
                    fill="#2485EC"
                    animate={{
                      opacity: [0.2, 1, 0.2],
                      scale: [0.5, 1.5, 0.5],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 3,
                    }}
                  />
                </svg>
              </motion.div>

              {/* Connecting lines between data points */}
              <motion.div
                className="absolute w-64 h-64"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 35,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.path
                    d="M128 64 L192 128 L128 192 L64 128 Z"
                    stroke="url(#connectGradient)"
                    strokeWidth="1"
                    fill="none"
                    strokeDasharray="3,3"
                    animate={{
                      strokeDashoffset: [0, -6],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  <defs>
                    <linearGradient id="connectGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#2485EC" stopOpacity="0.6" />
                      <stop offset="50%" stopColor="#FEC941" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.6" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>
            </div>
            
            {/* Analytics Dashboard SVG */}
            <div className="relative w-3/4 h-80 flex items-center justify-center z-10">
              <svg width="100%" height="100%" viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                {/* Dashboard Background */}
                <rect x="20" y="20" width="360" height="260" rx="12" fill="url(#dashboardBg)" stroke="url(#dashboardBorder)" strokeWidth="2"/>
                
                {/* Header */}
                <rect x="40" y="40" width="320" height="40" rx="8" fill="url(#headerGradient)"/>
                <text x="60" y="65" fill="white" fontSize="14" fontWeight="600"> Analytics Dashboard</text>
                
                {/* Chart Area */}
                <rect x="40" y="100" width="320" height="160" rx="8" fill="url(#chartBg)" stroke="url(#chartBorder)" strokeWidth="1"/>
                
                {/* Bar Chart */}
                <motion.rect
                  x="60" y="220" width="20" height="20"
                  fill="url(#barGradient1)"
                  animate={{
                    height: [20, 60, 40, 80, 20],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.rect
                  x="90" y="200" width="20" height="40"
                  fill="url(#barGradient2)"
                  animate={{
                    height: [40, 80, 60, 100, 40],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                />
                <motion.rect
                  x="120" y="180" width="20" height="60"
                  fill="url(#barGradient3)"
                  animate={{
                    height: [60, 100, 80, 120, 60],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
                <motion.rect
                  x="150" y="160" width="20" height="80"
                  fill="url(#barGradient1)"
                  animate={{
                    height: [80, 120, 100, 140, 80],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5,
                  }}
                />
                <motion.rect
                  x="180" y="140" width="20" height="100"
                  fill="url(#barGradient2)"
                  animate={{
                    height: [100, 140, 120, 160, 100],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2,
                  }}
                />
                <motion.rect
                  x="210" y="120" width="20" height="120"
                  fill="url(#barGradient3)"
                  animate={{
                    height: [120, 160, 140, 180, 120],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2.5,
                  }}
                />
                <motion.rect
                  x="240" y="100" width="20" height="140"
                  fill="url(#barGradient1)"
                  animate={{
                    height: [140, 180, 160, 200, 140],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 3,
                  }}
                />
                
                {/* Line Chart */}
                <motion.path
                  d="M60 200 Q90 180 120 160 T180 120 T240 100"
                  stroke="url(#lineGradient)"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  animate={{
                    pathLength: [0, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                
                {/* Data Points */}
                <motion.circle
                  cx="60" cy="200" r="4"
                  fill="#2485EC"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.circle
                  cx="120" cy="160" r="4"
                  fill="#FEC941"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                />
                <motion.circle
                  cx="180" cy="120" r="4"
                  fill="#ff00c8"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
                <motion.circle
                  cx="240" cy="100" r="4"
                  fill="#2485EC"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5,
                  }}
                />
                
                {/* Metrics Cards */}
                <rect x="280" y="110" width="60" height="30" rx="6" fill="url(#metricGradient1)"/>
                <text x="290" y="130" fill="white" fontSize="10" fontWeight="600">+23%</text>
                
                <rect x="280" y="150" width="60" height="30" rx="6" fill="url(#metricGradient2)"/>
                <text x="290" y="170" fill="white" fontSize="10" fontWeight="600">1.2K</text>
                
                <rect x="280" y="190" width="60" height="30" rx="6" fill="url(#metricGradient3)"/>
                <text x="290" y="210" fill="white" fontSize="10" fontWeight="600">89%</text>
                
                {/* Grid Lines */}
                <line x1="40" y1="200" x2="360" y2="200" stroke="url(#gridLine)" strokeWidth="1" opacity="0.3"/>
                <line x1="40" y1="180" x2="360" y2="180" stroke="url(#gridLine)" strokeWidth="1" opacity="0.3"/>
                <line x1="40" y1="160" x2="360" y2="160" stroke="url(#gridLine)" strokeWidth="1" opacity="0.3"/>
                <line x1="40" y1="140" x2="360" y2="140" stroke="url(#gridLine)" strokeWidth="1" opacity="0.3"/>
                <line x1="40" y1="120" x2="360" y2="120" stroke="url(#gridLine)" strokeWidth="1" opacity="0.3"/>
                
                {/* Definitions */}
                <defs>
                  <linearGradient id="dashboardBg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.1)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
                  </linearGradient>
                  <linearGradient id="dashboardBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#2485EC" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient id="headerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2485EC" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.8" />
                  </linearGradient>
                  <linearGradient id="chartBg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
                  </linearGradient>
                  <linearGradient id="chartBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#2485EC" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient id="barGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#2485EC" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.4" />
                  </linearGradient>
                  <linearGradient id="barGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#FEC941" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#FEC941" stopOpacity="0.4" />
                  </linearGradient>
                  <linearGradient id="barGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#ff00c8" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.4" />
                  </linearGradient>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2485EC" />
                    <stop offset="50%" stopColor="#FEC941" />
                    <stop offset="100%" stopColor="#ff00c8" />
                  </linearGradient>
                  <linearGradient id="metricGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2485EC" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.8" />
                  </linearGradient>
                  <linearGradient id="metricGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FEC941" />
                    <stop offset="100%" stopColor="#FEC941" stopOpacity="0.8" />
                  </linearGradient>
                  <linearGradient id="metricGradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ff00c8" />
                    <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.8" />
                  </linearGradient>
                  <linearGradient id="gridLine" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2485EC" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 